import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {KeyboardEventHandler} from 'react';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {AddUnitStore} from './parts/AddUnitStore';
import {DeleteUnitStore} from './parts/DeleteUnitStore';
import {EditUnitStore} from './parts/EditUnitStore';
import {MoveUnitStore} from './parts/MoveUnitStore';
import {ReorderUnitsStore} from './parts/ReorderUnitsStore';
import {X3LevelLoader} from './X3LevelLoader';

type Mode = 'View' | 'Add' | 'Edit' | 'Delete' | 'ReOrder' | 'Move';

export class X3LevelStore {
  readonly loader: X3LevelLoader;

  @observable
  mode: Mode;

  @observable.ref
  addUnit: AddUnitStore | null;
  @observable.ref
  editUnit: EditUnitStore | null;
  @observable.ref
  deleteUnit: DeleteUnitStore | null;
  @observable.ref
  reorder: ReorderUnitsStore | null;
  @observable.ref
  move: MoveUnitStore | null;

  private readonly disposers = new Disposers();

  constructor(readonly levelId: number) {
    makeObservable(this);
    this.mode = 'View';

    this.loader = new X3LevelLoader(levelId);
    this.disposers.add(this.loader);

    this.addUnit = this.editUnit = this.deleteUnit =
      this.move = this.reorder = null;

    this.setup();
  }

  @action
  private clear = () => {
    this.mode = 'View';
    this.addUnit = this.editUnit = this.deleteUnit =
      this.move = this.reorder = null;
  };

  @computed
  get isSaving(): boolean {
    return (this.addUnit?.save?.isPending === true ||
      this.editUnit?.save?.isPending === true ||
      this.reorder?.save?.isPending === true ||
      this.deleteUnit?.save?.isPending === true ||
      this.move?.save?.isPending === true
    );
  }

  @action
  beginAdd = () => {
    this.mode = 'Add';
    this.addUnit = new AddUnitStore(this.levelId);
  };

  @action
  completeAdd = () => {
    this.addUnit?.complete();
  };

  @action
  beginEdit = (unitId: number) => {
    this.mode = 'Edit';
    let unit = this.loader.unitById[unitId]!;
    this.editUnit = new EditUnitStore(unit);
  };

  @action
  completeEdit = () => {
    this.editUnit?.complete();
  };

  @action
  completeOnEnter: KeyboardEventHandler = (event) => {
    if (event.key !== 'Enter')
      return;
    if (this.mode === 'Add')
      this.completeAdd();
    if (this.mode === 'Edit')
      this.completeEdit();
  };

  @action
  beginMove = (unitId: number) => {
    this.mode = 'Move';
    const unit = this.loader.unitById[unitId];
    this.move = new MoveUnitStore(unit, this.loader.lookup!);
  };

  @action
  completeMove = () => {
    this.move?.complete();
  };

  @action
  beginDelete = (unitId: number) => {
    this.mode = 'Delete';
    this.deleteUnit = new DeleteUnitStore(unitId);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deleteUnit?.complete();
  };

  @action
  beginReorder = () => {
    this.mode = 'ReOrder';
    const unitIds = this.loader.units.map(x => x.Id);
    this.reorder = new ReorderUnitsStore(this.levelId, unitIds);
  };

  @action
  reorderUp = (index: number) => {
    this.reorder?.swap(index, index - 1);
  };

  @action
  reorderDown = (index: number) => {
    this.reorder?.swap(index, index + 1);
  };

  @action
  completeReorder = () => {
    this.reorder?.complete();
  };

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.clear();
  };

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.addUnit?.save?.state,
        this.editUnit?.save?.state,
        this.deleteUnit?.save?.state,
        this.reorder?.save?.state,
        this.move?.save?.state
      ],
      this.reloadOnSuccessResponse
    ));
  }

  @action
  private reloadOnSuccessResponse = () => {
    for (let state of [
      this.addUnit?.save,
      this.editUnit?.save,
      this.deleteUnit?.save,
      this.reorder?.save,
      this.move?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.loader.refresh();
        this.clear();
      }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
